import { useRef, useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { uploadPhoto, deletePhoto, getExpedientePhotos } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Trash, ArrowsClockwise, CheckCircle, Circle, Compass, Images } from "@phosphor-icons/react";

const SLOTS = [
  { key: "norte", labelKey: "photo_norte", group: "linderos" },
  { key: "sur", labelKey: "photo_sur", group: "linderos" },
  { key: "este", labelKey: "photo_este", group: "linderos" },
  { key: "oeste", labelKey: "photo_oeste", group: "linderos" },
  { key: "letrero_1", labelKey: "photo_letrero_1", group: "letrero" },
  { key: "letrero_2", labelKey: "photo_letrero_2", group: "letrero" },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PhotoSlot({ slot, exp, onChange }) {
  const { t } = useLang();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const dataUrl = exp.photos?.[slot.key];

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await uploadPhoto(exp.id, slot.key, b64);
      // Solo refrescamos photos (no todo el expediente) para no cargar memoria
      const fresh = await getExpedientePhotos(exp.id);
      onChange({ ...exp, photos: fresh.photos || {} });
      toast.success(t.saved);
    } catch (e) {
      toast.error(t.error);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deletePhoto(exp.id, slot.key);
      const fresh = await getExpedientePhotos(exp.id);
      onChange({ ...exp, photos: fresh.photos || {} });
    } catch (e) { toast.error(t.error); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid={`photo-slot-${slot.key}`}>
      <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-slate-700 font-semibold">
          {t[slot.labelKey]}
        </span>
        {dataUrl && (
          <Button
            data-testid={`remove-photo-${slot.key}`}
            size="sm" variant="ghost"
            onClick={remove}
            disabled={busy}
            className="h-6 px-1.5 text-slate-400 hover:text-red-600 rounded-sm"
          >
            <Trash size={12} />
          </Button>
        )}
      </div>
      {dataUrl ? (
        <div className="relative group">
          <img
            src={dataUrl}
            alt={slot.key}
            className="w-full h-44 object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/60 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              data-testid={`replace-camera-${slot.key}`}
              onClick={() => cameraRef.current?.click()}
              className="px-2.5 py-1.5 bg-white text-slate-900 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5 rounded-sm hover:bg-orange-500 hover:text-white"
            >
              <Camera size={12} /> Cámara
            </button>
            <button
              data-testid={`replace-gallery-${slot.key}`}
              onClick={() => galleryRef.current?.click()}
              className="px-2.5 py-1.5 bg-white text-slate-900 text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5 rounded-sm hover:bg-blue-600 hover:text-white"
            >
              <Images size={12} /> Galería
            </button>
          </div>
        </div>
      ) : (
        <div
          data-testid={`upload-photo-${slot.key}`}
          className="dropzone w-full h-44 flex flex-col items-center justify-center text-slate-500 gap-2"
        >
          <Camera weight="duotone" size={28} />
          <span className="text-xs font-mono uppercase tracking-widest text-slate-400">
            {busy ? "…" : "Agregar foto"}
          </span>
          <div className="flex items-center gap-1.5 mt-1">
            <button
              data-testid={`pick-camera-${slot.key}`}
              onClick={() => cameraRef.current?.click()}
              disabled={busy}
              className="px-2.5 py-1.5 border border-slate-300 bg-white text-xs font-semibold flex items-center gap-1.5 rounded-sm hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors disabled:opacity-50"
            >
              <Camera size={12} /> Cámara
            </button>
            <button
              data-testid={`pick-gallery-${slot.key}`}
              onClick={() => galleryRef.current?.click()}
              disabled={busy}
              className="px-2.5 py-1.5 border border-slate-300 bg-white text-xs font-semibold flex items-center gap-1.5 rounded-sm hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors disabled:opacity-50"
            >
              <Images size={12} /> Galería
            </button>
          </div>
        </div>
      )}
      {/* Input para CÁMARA (forzada en mobile) */}
      <input
        ref={cameraRef}
        data-testid={`file-input-camera-${slot.key}`}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }}
      />
      {/* Input para GALERÍA / ARCHIVOS (sin capture) — permite escoger del rollo */}
      <input
        ref={galleryRef}
        data-testid={`file-input-gallery-${slot.key}`}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}

export default function FotosTab({ exp, setExp }) {
  const { t } = useLang();

  // Cargar fotos al montar el tab (no vienen en el GET principal del expediente para ahorrar memoria)
  useEffect(() => {
    if (exp?.id && (!exp.photos || Object.keys(exp.photos).length === 0)) {
      getExpedientePhotos(exp.id).then((res) => {
        setExp((prev) => ({ ...prev, photos: res.photos || {} }));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exp?.id]);

  const done = SLOTS.filter((s) => !!exp.photos?.[s.key]).length;
  const total = SLOTS.length;
  const pct = Math.round((done / total) * 100);
  const missing = SLOTS.filter((s) => !exp.photos?.[s.key]);

  return (
    <div className="space-y-6" data-testid="fotos-tab">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">{t.photos_section_title}</h2>
        <p className="text-slate-600 mt-1">{t.photos_section_desc}</p>
      </div>

      {/* Checklist resumen — útil en campo para confirmar de un vistazo qué falta */}
      <div className="bg-white border border-slate-200" data-testid="fotos-checklist">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Compass weight="duotone" size={18} color="#f97316" />
            <div>
              <div className="coord-label">// CHECKLIST DE FOTOS</div>
              <div className="text-sm font-semibold text-slate-900">
                {done}/{total} cargadas
                {missing.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-orange-600">
                    Falta: {missing.map((m) => t[m.labelKey]).join(", ")}
                  </span>
                )}
                {missing.length === 0 && (
                  <span className="ml-2 text-xs font-normal text-emerald-600">¡Todas las fotos cargadas!</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 min-w-[140px]">
            <div className="flex-1 h-1.5 bg-slate-200 rounded-sm overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="font-mono text-xs text-slate-600 w-8 text-right">{pct}%</span>
          </div>
        </div>
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {SLOTS.map((s) => {
            const loaded = !!exp.photos?.[s.key];
            return (
              <div
                key={s.key}
                data-testid={`checklist-${s.key}`}
                className={`px-3 py-2 rounded-sm border flex items-center gap-2 ${
                  loaded
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-slate-50 border-slate-200"
                }`}
              >
                {loaded ? (
                  <CheckCircle weight="fill" size={18} className="text-emerald-500 shrink-0" />
                ) : (
                  <Circle weight="bold" size={18} className="text-slate-300 shrink-0" />
                )}
                <span className={`text-xs font-mono uppercase tracking-wide truncate ${loaded ? "text-emerald-700 font-bold" : "text-slate-500"}`}>
                  {t[s.labelKey]}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="coord-label">LINDEROS / BOUNDARIES — N · S · E · O</div>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SLOTS.filter(s => s.group === "linderos").map((slot) => (
            <PhotoSlot key={slot.key} slot={slot} exp={exp} onChange={setExp} />
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="coord-label">LETRERO / SURVEY SIGN</div>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SLOTS.filter(s => s.group === "letrero").map((slot) => (
            <PhotoSlot key={slot.key} slot={slot} exp={exp} onChange={setExp} />
          ))}
        </div>
      </div>
    </div>
  );
}
